<template>
  <component
    :is="component"
    :[attrName]="attrValue"
    :type="type"
    :target="component === 'a' ? '_blank' : undefined"
    class="inline-block rounded-md px-3 py-2 text-sm font-semibold shadow-xs relative cursor-pointer disabled:cursor-not-allowed text-center"
    :class="classes"
    :disabled="isDisabled">
    <span v-if="loading" class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <NtLoader :color="loadColor" loading />
    </span>
    <span class="inline-flex items-center gap-x-1.5" :class="{ invisible: loading }">
      <i v-if="icon" class="fa-fw fa-sharp" :class="[icon, iconRight ? 'order-2' : null]"></i>
      <slot />
    </span>
  </component>
</template>

<script>
import { RouterLink } from 'vue-router'
import NtLoader from './NtLoader.vue'
import { mapState } from 'pinia'
import { useSystemStore } from '@/stores/system'

export default {
  components: { NtLoader, RouterLink },
  props: {
    level: {
      type: String,
      default: 'secondary'
    },
    type: {
      type: String,
      default: 'button'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    to: {
      type: [String, Object]
    },
    href: {
      type: String
    },
    icon: {
      type: String,
      default: null
    },
    iconRight: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(useSystemStore, ['hub']),
    component() {
      if (!this.disabled) {
        if (this.to) {
          return 'RouterLink'
        }
        if (this.href) {
          return 'a'
        }
      }

      return 'button'
    },
    attrName() {
      return this.to ? 'to' : 'href'
    },
    attrValue() {
      return this.to || this.href
    },
    classes() {
      switch (this.level) {
        case 'red':
        case 'danger':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-red-600 text-white disabled:text-white/50 focus-visible:outline-red-600 dark:focus-visible:outline-red-500',
            this.component === 'button' ? 'hover:enabled:bg-red-500' : 'hover:bg-red-500'
          ]
        case 'orange':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-orange-600 text-white disabled:text-white/50 focus-visible:outline-orange-600 dark:focus-visible:outline-orange-500',
            this.component === 'button' ? 'hover:enabled:bg-orange-500' : 'hover:bg-orange-500'
          ]
        case 'amber':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-amber-600 text-white disabled:text-white/50 focus-visible:outline-amber-600 dark:focus-visible:outline-amber-500',
            this.component === 'button' ? 'hover:enabled:bg-amber-500' : 'hover:bg-amber-500'
          ]
        case 'yellow':
        case 'warning':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-yellow-600 text-white disabled:text-white/50 focus-visible:outline-yellow-600 dark:focus-visible:outline-yellow-500',
            this.component === 'button' ? 'hover:enabled:bg-yellow-500' : 'hover:bg-yellow-500'
          ]
        case 'lime':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-lime-600 text-white disabled:text-white/50 focus-visible:outline-lime-600 dark:focus-visible:outline-lime-500',
            this.component === 'button' ? 'hover:enabled:bg-lime-500' : 'hover:bg-lime-500'
          ]
        case 'green':
        case 'success':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-green-600 text-white disabled:text-white/50 focus-visible:outline-green-600 dark:focus-visible:outline-green-500',
            this.component === 'button' ? 'hover:enabled:bg-green-500' : 'hover:bg-green-500'
          ]
        case 'emerald':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-emerald-600 text-white disabled:text-white/50 focus-visible:outline-emerald-600 dark:focus-visible:outline-emerald-500',
            this.component === 'button' ? 'hover:enabled:bg-emerald-500' : 'hover:bg-emerald-500'
          ]
        case 'teal':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-teal-600 text-white disabled:text-white/50 focus-visible:outline-teal-600 dark:focus-visible:outline-teal-500',
            this.component === 'button' ? 'hover:enabled:bg-teal-500' : 'hover:bg-teal-500'
          ]
        case 'cyan':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-cyan-600 text-white disabled:text-white/50 focus-visible:outline-cyan-600 dark:focus-visible:outline-cyan-500',
            this.component === 'button' ? 'hover:enabled:bg-cyan-500' : 'hover:bg-cyan-500'
          ]
        case 'sky':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-sky-600 text-white disabled:text-white/50 focus-visible:outline-sky-600 dark:focus-visible:outline-sky-500',
            this.component === 'button' ? 'hover:enabled:bg-sky-500' : 'hover:bg-sky-500'
          ]
        case 'blue':
        case 'info':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-blue-600 text-white disabled:text-white/50 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-500',
            this.component === 'button' ? 'hover:enabled:bg-blue-500' : 'hover:bg-blue-500'
          ]
        case 'indigo':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 text-white disabled:text-white/50 focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-500',
            this.component === 'button' ? 'hover:enabled:bg-indigo-500' : 'hover:bg-indigo-500'
          ]
        case 'violet':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-violet-600 text-white disabled:text-white/50 focus-visible:outline-violet-600 dark:focus-visible:outline-violet-500',
            this.component === 'button' ? 'hover:enabled:bg-violet-500' : 'hover:bg-violet-500'
          ]
        case 'purple':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-purple-600 text-white disabled:text-white/50 focus-visible:outline-purple-600 dark:focus-visible:outline-purple-500',
            this.component === 'button' ? 'hover:enabled:bg-purple-500' : 'hover:bg-purple-500'
          ]
        case 'fuchsia':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-fuchsia-600 text-white disabled:text-white/50 focus-visible:outline-fuchsia-600 dark:focus-visible:outline-fuchsia-500',
            this.component === 'button' ? 'hover:enabled:bg-fuchsia-500' : 'hover:bg-fuchsia-500'
          ]
        case 'pink':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-pink-600 text-white disabled:text-white/50 focus-visible:outline-pink-600 dark:focus-visible:outline-pink-500',
            this.component === 'button' ? 'hover:enabled:bg-pink-500' : 'hover:bg-pink-500'
          ]
        case 'rose':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-rose-600 text-white disabled:text-white/50 focus-visible:outline-rose-600 dark:focus-visible:outline-rose-500',
            this.component === 'button' ? 'hover:enabled:bg-rose-500' : 'hover:bg-rose-500'
          ]
        case 'accent':
        case 'primary':
          return [
            'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 bg-accent-500 focus-visible:outline-accent-600 dark:focus-visible:outline-accent-500',
            this.component === 'button' ? 'hover:enabled:bg-accent-400' : 'hover:bg-accent-400',
            this.hub.colour_accent_text === 'text-white' ? 'text-white disabled:text-white/50' : null,
            this.hub.colour_accent_text === 'text-black' ? 'text-black disabled:text-black/50' : null,
            this.hub.colour_accent_text === 'text-base' ? 'text-accent-text disabled:text-accent-text/50' : null
          ]
      }

      return [
        'ring-1 ring-inset bg-white dark:bg-neutral-600 text-neutral-900 disabled:text-neutral-900/30 dark:text-white dark:disabled:text-white/30 ring-neutral-300 dark:ring-0',
        this.component === 'button'
          ? ' hover:enabled:bg-neutral-50 dark:hover:enabled:bg-neutral-500'
          : ' hover:bg-neutral-50 dark:hover:bg-neutral-500'
      ]
    },
    loadColor() {
      return this.level === 'secondary' ? 'secondary' : 'white'
    },
    isDisabled() {
      return this.disabled || this.loading
    }
  }
}
</script>
